import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeft, Landmark, Plus } from "lucide-react";
import DashboardCard from "../components/DashboardCard";
import DashboardCardSkeleton from "../components/DashboardCardSkeleton";
import DashboardEmptyState from "../components/DashboardEmptyState";
import DashboardPagination from "../components/DashboardPagination";
import {
  IFinancingInsight,
  IMarketIndices,
  IPaginationMeta,
  IPayment,
  IPropertyReturn,
} from "../types/ownerDashboard.types";
import {
  buildPropertyLabelMap,
  normalizeKenloPropertyId,
  resolveOwnerPropertyCardLabel,
} from "../utils/buildPropertyLabelMap";
import { KenloPropertyOption } from "../utils/getKenloPropertyOptions";
import FinancingCompactCard from "./FinancingCompactCard";
import FinancingInsightDetail from "./FinancingInsightDetail";
import RegisterFinancingModal from "./RegisterFinancingModal";
import EditFinancingModal from "./EditFinancingModal";

interface FinancingInsightsProps {
  financingInsights: IFinancingInsight[];
  financingPagination: IPaginationMeta;
  allFinancingInsights: IFinancingInsight[];
  propertyReturns: IPropertyReturn[];
  payments: IPayment[];
  propertyFilterOptions: KenloPropertyOption[];
  marketIndices: IMarketIndices;
  smDown: boolean;
  lgDown?: boolean;
  isLoading?: boolean;
  onPageChange: (page: number) => Promise<void>;
  onRefresh: () => Promise<void>;
}

function buildFinancingRegisterOptions(
  propertyReturns: IPropertyReturn[],
  financingInsights: IFinancingInsight[],
  propertyFilterOptions: KenloPropertyOption[],
  propertyLabelMap: Map<string, string>
): KenloPropertyOption[] {
  const registeredFinancingIds = new Set(
    financingInsights.map((item) => normalizeKenloPropertyId(item.kenlo_property_id))
  );

  return propertyReturns
    .filter(
      (item) => !registeredFinancingIds.has(normalizeKenloPropertyId(item.kenlo_property_id))
    )
    .map((item) => ({
      kenloPropertyId: normalizeKenloPropertyId(item.kenlo_property_id),
      label: resolveOwnerPropertyCardLabel(item, propertyFilterOptions, propertyLabelMap),
    }));
}

export default function FinancingInsights({
  financingInsights,
  financingPagination,
  allFinancingInsights,
  propertyReturns,
  payments,
  propertyFilterOptions,
  marketIndices,
  smDown,
  lgDown = false,
  isLoading = false,
  onPageChange,
  onRefresh,
}: FinancingInsightsProps) {
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [editingInsight, setEditingInsight] = useState<IFinancingInsight | null>(null);
  const [selectedInsightId, setSelectedInsightId] = useState<number | null>(null);
  const padding = smDown ? "0.875rem" : lgDown ? "1rem" : "1.125rem";

  const propertyLabelMap = useMemo(
    () => buildPropertyLabelMap(payments),
    [payments]
  );

  const resolveFinancingLabel = (source: IFinancingInsight | IPropertyReturn) =>
    resolveOwnerPropertyCardLabel(source, propertyFilterOptions, propertyLabelMap);

  const registerOptions = useMemo(
    () =>
      buildFinancingRegisterOptions(
        propertyReturns,
        allFinancingInsights,
        propertyFilterOptions,
        propertyLabelMap
      ),
    [propertyReturns, allFinancingInsights, propertyFilterOptions, propertyLabelMap]
  );

  const selectedInsight = useMemo(
    () =>
      selectedInsightId == null
        ? null
        : allFinancingInsights.find((insight) => insight.id === selectedInsightId) ??
          financingInsights.find((insight) => insight.id === selectedInsightId) ??
          null,
    [allFinancingInsights, financingInsights, selectedInsightId]
  );

  useEffect(() => {
    if (
      selectedInsightId != null &&
      !allFinancingInsights.some((insight) => insight.id === selectedInsightId) &&
      !financingInsights.some((insight) => insight.id === selectedInsightId)
    ) {
      setSelectedInsightId(null);
    }
  }, [allFinancingInsights, financingInsights, selectedInsightId]);

  const hasFinancingInsights =
    allFinancingInsights.length > 0 || financingInsights.length > 0;
  const canRegisterFinancing = registerOptions.length > 0;
  const isDetailView = selectedInsight != null;

  if (isLoading && !hasFinancingInsights) {
    return <DashboardCardSkeleton minHeight="min-h-[24rem]" lines={6} />;
  }

  return (
    <>
      <DashboardCard padding={padding} className="flex h-full min-h-0 min-w-0 flex-col">
        <div className="flex min-h-0 min-w-0 w-full flex-1 flex-col gap-4">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Landmark className="h-5 w-5 shrink-0 text-[#6ea8ff]" />
                  <h2 className="text-base font-bold text-[#f0f6fc] sm:text-lg">Financiamento</h2>
                </div>
                <p className="mt-1 text-xs text-[#8b949e] sm:text-sm">
                  {isDetailView
                    ? "Detalhes do financiamento selecionado"
                    : "Relação entre aluguel e financiamento"}
                </p>
              </div>

              <div className="flex shrink-0 items-center gap-2 self-start">
                {isDetailView && (
                  <button
                    type="button"
                    onClick={() => setSelectedInsightId(null)}
                    className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.08] px-3 py-1.5 text-xs font-semibold text-[#8b949e] transition-colors hover:border-[#6ea8ff]/35 hover:bg-[#6ea8ff]/10 hover:text-[#6ea8ff]"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Voltar
                  </button>
                )}

                {canRegisterFinancing && !isDetailView && (
                  <button
                    type="button"
                    onClick={() => setIsRegisterModalOpen(true)}
                    className="inline-flex items-center justify-center gap-1.5 rounded-full border border-[#6ea8ff]/35 bg-[#6ea8ff]/10 px-4 py-2 text-xs font-semibold text-[#6ea8ff] transition-colors hover:bg-[#6ea8ff]/20 sm:text-sm"
                  >
                    <Plus className="h-4 w-4" />
                    Cadastrar financiamento
                  </button>
                )}
              </div>
            </div>
          </div>

          {!isLoading && !hasFinancingInsights && (
            <DashboardEmptyState
              title="Nenhum financiamento cadastrado"
              description="Informe o financiamento para estimar quando o aluguel líquido passará a cobrir a parcela do imóvel."
              action={
                canRegisterFinancing ? (
                  <button
                    type="button"
                    onClick={() => setIsRegisterModalOpen(true)}
                    className="rounded-full bg-[#6ea8ff] px-4 py-2 text-xs font-bold text-[#0d1117] transition-opacity hover:opacity-90"
                  >
                    Cadastrar financiamento
                  </button>
                ) : undefined
              }
              footer={
                !canRegisterFinancing ? (
                  <p className="text-xs text-[#6e7681]">
                    Cadastre primeiro o valor do imóvel em Rendimentos para habilitar esta
                    projeção.
                  </p>
                ) : undefined
              }
            />
          )}

          {hasFinancingInsights && !isDetailView && (
            <>
              {isLoading ? (
                <div className="grid min-w-0 gap-2 sm:grid-cols-2 sm:gap-2.5">
                  <div className="h-24 animate-pulse rounded-2xl bg-white/[0.06]" />
                  <div className="h-24 animate-pulse rounded-2xl bg-white/[0.06]" />
                </div>
              ) : (
                <div
                  className={`grid min-w-0 gap-2 sm:gap-2.5 ${
                    smDown ? "grid-cols-1" : "grid-cols-2"
                  }`}
                >
                  {financingInsights.map((insight) => (
                    <FinancingCompactCard
                      key={insight.id}
                      insight={insight}
                      propertyLabel={resolveFinancingLabel(insight)}
                      isSelected={selectedInsightId === insight.id}
                      onSelect={() => setSelectedInsightId(insight.id)}
                    />
                  ))}
                </div>
              )}

              <DashboardPagination
                currentPage={financingPagination.current_page}
                totalPages={financingPagination.last_page}
                onPageChange={onPageChange}
              />
            </>
          )}

          {hasFinancingInsights && selectedInsight && (
            <FinancingInsightDetail
              insight={selectedInsight}
              propertyLabel={resolveFinancingLabel(selectedInsight)}
              ipca12mAccumulatedPct={marketIndices.ipca_12m_accumulated_pct}
              onEdit={() => setEditingInsight(selectedInsight)}
            />
          )}
        </div>
      </DashboardCard>

      {isRegisterModalOpen && (
        <RegisterFinancingModal
          options={registerOptions}
          onClose={() => setIsRegisterModalOpen(false)}
          onSuccess={onRefresh}
        />
      )}

      {editingInsight && (
        <EditFinancingModal
          insight={editingInsight}
          propertyLabel={resolveFinancingLabel(editingInsight)}
          onClose={() => setEditingInsight(null)}
          onSuccess={onRefresh}
        />
      )}
    </>
  );
}
